// frontend/src/lib/socialLogin.js
//
// Capgo SocialLogin plugin initializer + platform-detection helpers.
//
// ⚠️ Uses STATIC imports — dynamic `await import("@capgo/...")` hangs inside
// the Capacitor iOS WebView (chunks served via capacitor://localhost don't
// resolve in some versions of WKWebView). The plugin's JS side is tiny so
// bundling it everywhere costs nothing.
//
// Env vars / build-time config:
//   REACT_APP_GOOGLE_IOS_CLIENT_ID  — iOS OAuth client (different from web).
//   REACT_APP_GOOGLE_WEB_CLIENT_ID  — existing web OAuth client.

import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";

let _initPromise = null;

export function isNativeIos() {
  return Capacitor.getPlatform() === "ios";
}

export function isNative() {
  return Capacitor.isNativePlatform();
}

/**
 * Lazy + idempotent initializer. Safe to call on web (no-op) and safe
 * to call repeatedly — only the first call performs the work.
 *
 * Returns the initialised config so callers can sanity-check that the
 * required iOSClientId was actually present at build time.
 */
export async function ensureSocialLoginInitialized() {
  if (_initPromise) return _initPromise;

  if (!isNativeIos()) {
    _initPromise = Promise.resolve({ skipped: "not-native" });
    return _initPromise;
  }

  _initPromise = (async () => {
    const googleIosClientId = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID;
    const googleWebClientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;

    const config = {};
    if (googleIosClientId) {
      config.google = {
        iOSClientId: googleIosClientId,
        ...(googleWebClientId ? { iOSServerClientId: googleWebClientId } : {}),
        mode: "online",
      };
    }
    config.apple = {};

    // Wrap in a timeout so we never hang the UI on a misconfigured plugin.
    // If initialize() doesn't resolve in 8s, the native pod is most likely
    // not installed in this .ipa — surface it loudly so we can debug.
    await Promise.race([
      SocialLogin.initialize(config),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(
          "SocialLogin.initialize() timed out — Capgo native pod missing from .ipa? Check CI 'Pod install' step + Podfile.lock"
        )), 8000),
      ),
    ]);
    return { ok: true, providers: Object.keys(config) };
  })();

  return _initPromise;
}

/** Returns the plugin singleton on native; null on web. */
export function getSocialLogin() {
  if (!isNativeIos()) return null;
  return SocialLogin;
}

/** Wraps a plugin login() call in a 45s timeout so the UI never freezes
 * if the native sheet fails to present (eg. entitlement missing). */
export function withLoginTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(
        "Sign-in sheet did not respond. Try again, or use email & password."
      )), 45000),
    ),
  ]);
}
